import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '@/layouts/admin-layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'

interface AgentSkill {
  id: number
  slug: string
  name: string
  description: string
  skill_type: string
  status: string
  submission_count: number
  usage_count: number
  applicable_contexts: string[]
  django_stack: boolean
  reviewed_by: string | null
  reviewed_at: string | null
  rejection_reason: string | null
  created_at: string
}

interface Pagination {
  page: number
  count: number
  pages: number
  last: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface AdminAgentSkillsIndexProps {
  skills: AgentSkill[]
  pagy: Pagination
  filters: { status: string } & Record<string, string>
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Index({ skills, pagy, filters }: AdminAgentSkillsIndexProps) {
  const { t } = useTranslation('admin')
  const [searchQuery, setSearchQuery] = useState('')

  const getStatusBadge = (status: string) => {
    const statusVariants: Record<string, 'warning' | 'success' | 'destructive' | 'secondary'> = {
      'pending': 'warning',
      'active': 'success',
      'rejected': 'destructive',
    }

    return (
      <Badge variant={statusVariants[status] || 'secondary'}>
        {t(`agent_skills.status.${status}`)}
      </Badge>
    )
  }

  const getTypeBadge = (skillType: string) => {
    return (
      <Badge variant="secondary" className="text-xs">
        {t(`agent_skills.types.${skillType}`)}
      </Badge>
    )
  }

  const handleStatusChange = (value: string) => {
    const params: Record<string, string> = { status: value }
    if (searchQuery) params['q[name_or_description_cont]'] = searchQuery
    router.get('/admin/agent_skills', params, { preserveState: true })
  }

  const handleSearch = () => {
    const params: Record<string, string> = { status: filters.status || 'all' }
    if (searchQuery) params['q[name_or_description_cont]'] = searchQuery
    router.get('/admin/agent_skills', params, { preserveState: true })
  }

  const handleReset = () => {
    setSearchQuery('')
    router.get('/admin/agent_skills', {}, { preserveState: true })
  }

  const buildParams = (overrides: Record<string, string | null> = {}) => {
    const params: Record<string, string> = { status: filters.status || 'pending' }
    if (searchQuery) params['q[name_or_description_cont]'] = searchQuery
    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') {
        delete params[key]
      } else {
        params[key] = value
      }
    }
    return params
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col py-2">
          <div className="px-3">
            {/* Filter Bar */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm font-semibold whitespace-nowrap">
                {t('agent_skills.index.titleWithCount', { count: pagy.count })}
              </span>
              <div className="w-px h-4 bg-border" />
              <Select
                value={filters.status || 'pending'}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger className="h-7 text-xs w-28">
                  <SelectValue placeholder={t('agent_skills.index.filters.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('agent_skills.index.filters.allStatuses')}</SelectItem>
                  <SelectItem value="pending">{t('agent_skills.status.pending')}</SelectItem>
                  <SelectItem value="active">{t('agent_skills.status.active')}</SelectItem>
                  <SelectItem value="rejected">{t('agent_skills.status.rejected')}</SelectItem>
                </SelectContent>
              </Select>
              <div className="w-px h-4 bg-border" />
              <Input
                type="text"
                placeholder={t('agent_skills.index.filters.searchPlaceholder')}
                className="h-7 text-xs w-48"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} className="h-7 px-2 text-xs">
                {t('agent_skills.index.buttons.search')}
              </Button>
              <Button onClick={handleReset} variant="ghost" className="h-7 px-2 text-xs">
                {t('agent_skills.index.buttons.reset')}
              </Button>
              <div className="flex-1" />
              <Button asChild className="h-7 px-2 text-xs">
                <Link href="/admin/agent_skills/new">{t('agent_skills.index.buttons.create')}</Link>
              </Button>
            </div>

            {/* Skills Table */}
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)', minHeight: '400px' }}>
                <table className="w-full">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('agent_skills.index.table.name')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('agent_skills.index.table.type')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('agent_skills.index.table.stack')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('agent_skills.index.table.submissions')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('agent_skills.index.table.usage')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('agent_skills.index.table.status')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('agent_skills.index.table.created')}
                      </th>
                      <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                        {t('agent_skills.index.table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {skills.length > 0 ? (
                      skills.map((skill) => (
                        <tr key={skill.id} className="hover:bg-muted transition-colors">
                          <td className="px-3 py-1.5">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-sm font-medium text-foreground">
                                {skill.name}
                              </span>
                              <span className="text-xs text-muted-foreground truncate max-w-md">
                                {skill.description}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            {getTypeBadge(skill.skill_type)}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Badge variant={skill.django_stack ? 'default' : 'secondary'} className="text-xs">
                              {skill.django_stack ? 'Django' : 'Rails'}
                            </Badge>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-sm font-medium text-foreground">
                              {skill.submission_count}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-sm text-muted-foreground">
                              {skill.usage_count}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            {getStatusBadge(skill.status)}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-sm text-foreground">
                              {new Date(skill.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <Link
                              href={`/admin/agent_skills/${skill.id}`}
                              className="text-sm text-blue-600 hover:text-blue-900"
                            >
                              {t('agent_skills.index.table.review')}
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-3 py-8 text-center text-sm text-muted-foreground">
                          {t('agent_skills.index.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                  <span className="text-muted-foreground">
                    {pagy.from}-{pagy.to} of {pagy.count}
                  </span>
                  <div className="flex gap-1">
                    {pagy.prev && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get('/admin/agent_skills', { ...buildParams(), page: pagy.prev }, { preserveState: true })}
                      >
                        {t('agent_skills.index.pagination.previous')}
                      </Button>
                    )}
                    {pagy.next && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get('/admin/agent_skills', { ...buildParams(), page: pagy.next }, { preserveState: true })}
                      >
                        {t('agent_skills.index.pagination.next')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
